import type { TextFieldProps } from "@mui/material/TextField";
import TextField from "@mui/material/TextField";
import type { ControllerProps, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";

type ControlledTextFieldProps<T extends FieldValues = FieldValues> = Omit<
  ControllerProps<T>,
  "render"
> &
  TextFieldProps & {
    formatValue?: (value: unknown) => string;
  };

export function ControlledTextField<T extends FieldValues>({
  control,
  name,
  rules,
  defaultValue,
  className,
  type,
  disabled,
  formatValue,
  ...props
}: ControlledTextFieldProps<T>) {
  // There seams to be a bug in react hook form when disabled is false
  // instead of undefined. When disabled is false fields are marked as dirty
  // although the fields are not changed.
  const disabledFixed = disabled || undefined;
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      disabled={disabledFixed}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => {
        const displayValue = formatValue
          ? formatValue(field.value)
          : (field.value ?? "");
        return (
          <TextField
            // This will avoid changing the value when the input is focused and the user scrolls
            onWheel={
              type === "number"
                ? (e) => (e.target as HTMLElement).blur()
                : undefined
            }
            {...props}
            {...field}
            value={displayValue}
            type={type}
            ref={null}
            inputRef={field.ref}
            error={!!fieldState.error}
            className={className}
            helperText={disabledFixed ? null : fieldState.error?.message}
          />
        );
      }}
    />
  );
}
